import { useEffect, useMemo, useState } from 'react';
import { FaBullseye, FaChartBar, FaLightbulb, FaLeaf } from 'react-icons/fa';
import { Header } from 'pages/HomePublic/components/Header';
import { useAuth } from 'context/AuthContext';
import { fetchMinhasAvaliacoesEtapa2 } from 'services/avaliacao-etapa2.service';
import type {
  AvaliacaoEtapa2,
  AvaliacaoIniciativa,
  CriterioKey,
} from 'types/etapa2';
import { getCategoriaLabel } from 'utils/iniciativa-categoria';
import './style.scss';

interface SignificadoFaixa {
  range: string;
  descricao: string;
}

interface CriterioDefinicao {
  key: CriterioKey;
  titulo: string;
  descricao: string;
  icone: React.ReactNode;
  faixas: SignificadoFaixa[];
}

const CRITERIOS: CriterioDefinicao[] = [
  {
    key: 'alinhamentoEstrategico',
    titulo: 'Alinhamento Estratégico',
    descricao: 'Avalia o quanto a iniciativa contribui para os direcionadores estratégicos do Banco',
    icone: <FaBullseye size={28} aria-hidden="true" />,
    faixas: [
      { range: '0-2', descricao: 'Não contribui para o direcionador estratégico' },
      { range: '3-4', descricao: 'Contribuição mínima ou indireta' },
      { range: '5-6', descricao: 'Contribuição moderada, mas não essencial' },
      { range: '7-8', descricao: 'Contribuição relevante e alinhada' },
      { range: '9-10', descricao: 'Contribuição essencial e completamente vinculada ao direcionador estratégico' },
    ],
  },
  {
    key: 'impactoMensuravel',
    titulo: 'Impacto Mensurável',
    descricao: 'Considera os resultados concretos obtidos pela iniciativa, com evidências quantitativas ou qualitativas',
    icone: <FaChartBar size={28} aria-hidden="true" />,
    faixas: [
      { range: '0-2', descricao: 'Sem resultados mensuráveis' },
      { range: '3-4', descricao: 'Resultados pouco significativos' },
      { range: '5-6', descricao: 'Resultados moderados, com impacto limitado' },
      { range: '7-8', descricao: 'Resultados relevantes e bem evidenciados' },
      { range: '9-10', descricao: 'Resultados financeiros expressivos, com forte impacto comprovado' },
    ],
  },
  {
    key: 'inovacaoCriatividade',
    titulo: 'Inovação e Criatividade',
    descricao: 'Analisa o grau de originalidade da iniciativa, uso de novas tecnologias ou abordagens diferenciadas',
    icone: <FaLightbulb size={28} aria-hidden="true" />,
    faixas: [
      { range: '0-2', descricao: 'Solução comum, sem inovação' },
      { range: '3-4', descricao: 'Pouca inovação, somente melhorias incrementais' },
      { range: '5-6', descricao: 'Algum grau de inovação, mas não disruptiva' },
      { range: '7-8', descricao: 'Inovação disruptiva em processo ou ambiente complexo' },
      { range: '9-10', descricao: 'Inovação transformacional para o posicionamento do Banco no mercado e na sociedade' },
    ],
  },
  {
    key: 'escalabilidadeSustentabilidade',
    titulo: 'Escalabilidade e Sustentabilidade',
    descricao: 'Verifica se a iniciativa pode ser replicada em outras áreas e se seus resultados são sustentáveis no longo prazo',
    icone: <FaLeaf size={28} aria-hidden="true" />,
    faixas: [
      { range: '0-2', descricao: 'Não escalável nem sustentável' },
      { range: '3-4', descricao: 'Escalabilidade ou sustentabilidade limitada' },
      { range: '5-6', descricao: 'Possível replicação com ajustes' },
      { range: '7-8', descricao: 'Boa escalabilidade e manutenção viável' },
      { range: '9-10', descricao: 'Altamente escalável e sustentável no longo prazo' },
    ],
  },
];

function getSignificado(faixas: SignificadoFaixa[], nota: number): string {
  const faixa = faixas.find(({ range }) => {
    const [start, end] = range.split('-').map(Number);
    return nota >= start && nota <= end;
  });
  return faixa ? `${faixa.range}: ${faixa.descricao}` : '';
}

const CATEGORIA_BADGE_MODIFIER: Record<string, string> = {
  'ECBB - Clientes': 'ecbb-cliente',
  'ECBB - Pessoas e Cultura': 'ecbb-cultura',
  'ECBB - ASG': 'ecbb-asg',
  'ECBB - Financeira': 'ecbb-financeira',
  'ECBB - Transformação Digital e Processos': 'ecbb-tdigital',
  'Joga Junto': 'joga-junto',
};

function getBadgeModifier(categoria: string): string {
  return CATEGORIA_BADGE_MODIFIER[categoria] ?? 'default';
}

const formatadorData = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

interface VotoFlatten {
  uniqueKey: string;
  avaliacaoId: string;
  category: string;
  iniciativaId: string;
  iniciativaTitulo: string;
  enviadoEm: number;
  notas: AvaliacaoIniciativa['notas'];
  total: number;
}

const flattenAvaliacoes = (docs: AvaliacaoEtapa2[]): VotoFlatten[] =>
  docs.flatMap((doc) =>
    doc.avaliacoes.map((av) => ({
      uniqueKey: `${doc._id}-${av.iniciativaId}`,
      avaliacaoId: doc._id,
      category: doc.category,
      iniciativaId: av.iniciativaId,
      iniciativaTitulo: av.iniciativaTitulo,
      enviadoEm: doc.enviadoEm,
      notas: av.notas,
      total: av.total,
    }))
  );

export function MeusVotosEtapa2() {
  const { user } = useAuth();
  const [docs, setDocs] = useState<AvaliacaoEtapa2[]>([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [categoriaFiltro, setCategoriaFiltro] = useState<string>('');
  const [busca, setBusca] = useState('');

  useEffect(() => {
    if (!user?._id) return;
    let canceled = false;
    setLoading(true);
    setErro(null);
    fetchMinhasAvaliacoesEtapa2(user._id)
      .then((res) => {
        if (canceled) return;
        setDocs(res);
      })
      .catch((err: unknown) => {
        if (canceled) return;
        setErro(
          err instanceof Error
            ? err.message
            : 'Erro ao carregar suas avaliações'
        );
      })
      .finally(() => {
        if (!canceled) setLoading(false);
      });
    return () => {
      canceled = true;
    };
  }, [user?._id]);

  const categoriasUsuario = useMemo<string[]>(
    () => (user?.extra?.categories ?? []).filter((c) => c !== 'ELBB'),
    [user]
  );

  const votos = useMemo(() => flattenAvaliacoes(docs), [docs]);

  const votosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return votos.filter((v) => {
      const matchCategoria =
        categoriaFiltro === '' || v.category === categoriaFiltro;
      const matchBusca =
        termo.length === 0 ||
        v.iniciativaTitulo.toLowerCase().includes(termo);
      return matchCategoria && matchBusca;
    });
  }, [votos, categoriaFiltro, busca]);

  return (
    <div id="meus-votos-etapa-2-wrapper">
      <Header currentPage="meus-votos-etapa-2" />

      <section className="meus-votos-etapa-2-hero">
        <div className="meus-votos-etapa-2-hero__content">
          <h1 className="meus-votos-etapa-2-hero__title">Meus votos no Prêmio BB 2026</h1>
          <p className="meus-votos-etapa-2-hero__description">
            Confira aqui as iniciativas que você votou
          </p>
        </div>
      </section>

      <main className="meus-votos-etapa-2-main">
        <h2 className="meus-votos-etapa-2-main__title">Esses foram os seus votos!</h2>

        <div className="meus-votos-etapa-2-filters">
          <select
            value={categoriaFiltro}
            onChange={(event) => setCategoriaFiltro(event.target.value)}
            className="meus-votos-etapa-2-filters__select"
          >
            <option value="">Todas as categorias</option>
            {categoriasUsuario.map((cat) => (
              <option key={cat} value={cat}>
                {getCategoriaLabel(cat)}
              </option>
            ))}
          </select>
          <input
            type="search"
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            placeholder="O que você procura?"
            className="meus-votos-etapa-2-filters__search"
          />
        </div>

        {loading && (
          <p className="meus-votos-etapa-2-list__empty">Carregando suas avaliações…</p>
        )}
        {erro && !loading && (
          <p className="meus-votos-etapa-2-list__empty">{erro}</p>
        )}

        <section className="meus-votos-etapa-2-list">
          {!loading && !erro && votosFiltrados.length === 0 && (
            <p className="meus-votos-etapa-2-list__empty">
              Nenhuma iniciativa encontrada para os filtros selecionados.
            </p>
          )}

          {!loading && !erro &&
            votosFiltrados.map((voto) => {
              const categoriaLabel = getCategoriaLabel(voto.category);
              return (
                <article key={voto.uniqueKey} className="meus-votos-etapa-2-card">
                  <div className="meus-votos-etapa-2-card__header">
                    <h3 className="meus-votos-etapa-2-card__title">{voto.iniciativaTitulo}</h3>
                    <span
                      className={`meus-votos-etapa-2-card__badge meus-votos-etapa-2-card__badge--${getBadgeModifier(categoriaLabel)}`}
                    >
                      {categoriaLabel}
                    </span>
                  </div>

                  <div className="meus-votos-etapa-2-card__info-box">
                    <div className="meus-votos-etapa-2-card__info-item">
                      <span className="meus-votos-etapa-2-card__info-label">Categoria:</span>
                      <span className="meus-votos-etapa-2-card__info-value">
                        {categoriaLabel}
                      </span>
                    </div>
                    <div className="meus-votos-etapa-2-card__info-item">
                      <span className="meus-votos-etapa-2-card__info-label">Avaliada em:</span>
                      <span className="meus-votos-etapa-2-card__info-value">
                        {formatadorData.format(new Date(voto.enviadoEm))}
                      </span>
                    </div>
                  </div>

                  <div className="meus-votos-etapa-2-card__evaluation">
                    <h4 className="meus-votos-etapa-2-card__evaluation-title">Sua avaliação</h4>
                    <div className="meus-votos-etapa-2-card__evaluation-header">
                      <p className="meus-votos-etapa-2-card__evaluation-hint">
                        Pontuação dada para cada critério (0 a 10)
                      </p>
                    </div>

                    <div className="meus-votos-etapa-2-criterios">
                      {CRITERIOS.map((criterio) => {
                        const valor = voto.notas[criterio.key as CriterioKey];
                        return (
                          <div key={criterio.key} className="meus-votos-etapa-2-criterio">
                            <div className="meus-votos-etapa-2-criterio__icon" aria-hidden="true">
                              {criterio.icone}
                            </div>
                            <h5 className="meus-votos-etapa-2-criterio__title">{criterio.titulo}</h5>
                            <p className="meus-votos-etapa-2-criterio__description">
                              {criterio.descricao}
                            </p>
                            <div
                              className="meus-votos-etapa-2-criterio__scale"
                              role="radiogroup"
                              aria-label={`Nota de ${criterio.titulo}`}
                            >
                              {Array.from({ length: 11 }).map((_, nota) => {
                                const isSelected = valor === nota;
                                return (
                                  <span
                                    key={nota}
                                    className={`meus-votos-etapa-2-criterio__score ${
                                      isSelected ? 'is-selected' : ''
                                    }`}
                                    data-tooltip={getSignificado(criterio.faixas, nota)}
                                    aria-checked={isSelected}
                                    role="radio"
                                  >
                                    {nota}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="meus-votos-etapa-2-card__total">Total: {voto.total} pontos</div>
                </article>
              );
            })}
        </section>
      </main>
    </div>
  );
}
